import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { api, extractError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Alert, AlertDescription } from "../../components/ui/alert";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "../../components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import {
  Banknote, Building2, Landmark, RefreshCw, Trash2, Plug, ShieldCheck,
  CheckCircle2, AlertTriangle, Loader2, Wand2, Activity, ChevronDown, ChevronUp,
  CalendarClock, Webhook,
} from "lucide-react";
import { toast } from "sonner";

// Lazy import so `react-plaid-link` is only fetched when admin actually opens
// the live-mode Connect flow.
const PlaidLinkLauncher = lazy(() => import("./PlaidLinkLauncher"));

const NONE = "__none__"; // shadcn Select cannot use empty-string value

function PageHeader({ title, subtitle, action }) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function StatusBanner({ status, loading, onRefresh }) {
  const mode = status?.mode || "—";
  const env = status?.env || "—";
  const isMock = mode === "mock";
  return (
    <Card data-testid="plaid-status-banner" className="mb-6 border-slate-200">
      <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className={`inline-flex h-9 w-9 items-center justify-center rounded-full ${
            isMock ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"
          }`}>
            <Plug className="h-4 w-4" />
          </span>
          <div>
            <p className="text-sm font-semibold text-slate-900">
              Plaid is running in{" "}
              <span className="font-mono" data-testid="plaid-mode">{mode}</span>{" "}
              mode · env <span className="font-mono">{env}</span>
            </p>
            <p className="text-xs text-slate-500">
              {isMock
                ? "Sandbox/mock client active. Connect Bank will simulate a sandbox institution."
                : "Live Plaid client active. Connect Bank opens the Plaid Link flow."}
              {" · "}
              <span className={status?.encryption_key_set ? "text-emerald-700" : "text-rose-700"}>
                <ShieldCheck className="mr-0.5 inline h-3 w-3" />
                {status?.encryption_key_set ? "Token encryption configured" : "Encryption key missing"}
              </span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="bg-slate-100 text-slate-700" data-testid="plaid-item-count">
            {status?.item_count ?? 0} linked
          </Badge>
          <Button variant="ghost" size="sm" onClick={onRefresh} disabled={loading} data-testid="plaid-refresh-status-btn">
            <RefreshCw className={`mr-1 h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Plaid Link `metadata.status` values that mean the user actively reached a
// real step and then bailed out (i.e. a true "cancel"). When status is null
// or anything else (e.g. dimmed-overlay teardown), we stay silent.
const DEFINITIVE_EXIT_STATUSES = new Set([
  "requires_credentials",
  "requires_account_selection",
  "requires_questions",
  "requires_selections",
  "requires_recaptcha",
  "requires_code",
  "requires_oauth",
  "choose_device",
  "institution_not_found",
]);

function ConnectBankDialog({ open, onOpenChange, mode, onConnected, onRequestLiveLink }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) { setError(""); setSubmitting(false); }
  }, [open]);

  const exchangeWithMock = async () => {
    setSubmitting(true); setError("");
    try {
      const r = await api.post("/plaid/exchange-public-token", { public_token: "mock-public-token" });
      toast.success(`Linked ${r.data?.institution_name || "institution"} (${r.data?.accounts?.length || 0} accounts)`);
      onConnected?.(r.data);
      onOpenChange(false);
    } catch (e) { setError(extractError(e)); }
    finally { setSubmitting(false); }
  };

  const startLiveLink = async () => {
    // IMPORTANT: Plaid Link must NOT render inside a Radix Dialog — Radix sets
    // `pointer-events: none` on <body> while a modal dialog is open, which
    // propagates to the Plaid Link iframe and makes the entire screen
    // unclickable (phone-entry field can't focus, "Continue without phone
    // number" can't be clicked). We close THIS dialog first so Radix releases
    // the body lock, then let the parent fetch the link token and mount the
    // launcher inline.
    setSubmitting(true); setError("");
    onOpenChange(false);
    try {
      await onRequestLiveLink?.();
    } catch (e) {
      // Dialog is already gone — surface the error as a toast instead of
      // setError (which would never be seen).
      toast.error(extractError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const isMock = mode === "mock";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="plaid-connect-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            <Banknote className="mr-1 inline h-4 w-4 -translate-y-0.5" />
            Connect bank via Plaid
          </DialogTitle>
          <DialogDescription>
            {isMock
              ? "Mock mode is active. Confirm to link the bundled sandbox institution."
              : "Live mode is active. Plaid Link will open on top of this page."}
          </DialogDescription>
        </DialogHeader>

        {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

        {isMock && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="plaid-mock-notice">
            <p className="font-medium">Sandbox institution</p>
            <p className="mt-1 text-xs">
              We&apos;ll create a Chase Sandbox item with two accounts (mask 9001 / 9999).
              The mask-9001 account auto-maps to the seeded EFS Fuel card.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          {isMock ? (
            <Button
              onClick={exchangeWithMock}
              disabled={submitting}
              data-testid="plaid-connect-mock-btn"
              className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90">
              {submitting ? "Connecting…" : "Confirm & connect sandbox"}
            </Button>
          ) : (
            <Button
              onClick={startLiveLink}
              disabled={submitting}
              data-testid="plaid-connect-live-btn"
              className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90">
              {submitting ? "Preparing…" : "Open Plaid Link"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Inline panel that hosts the live Plaid Link launcher OUTSIDE any Radix
 * Dialog. This is the only safe way to launch Plaid Link from a shadcn/Radix
 * app — see the "Plaid Link blocked by Radix Dialog" known conflict.
 *
 * Lifecycle:
 *   parent fetches link_token  →  sets `state.token`
 *   PlaidLinkLauncher auto-opens the Plaid iframe
 *   user finishes flow         →  onPublicToken  →  parent exchanges  →  unmounts panel
 *   user cancels               →  onExit fires   →  panel shows retry hint
 *   user clicks Dismiss        →  panel unmounts
 */
function LiveLinkPanel({ state, onPublicToken, onExit, onDismiss }) {
  if (!state) return null;
  const isFetching = !!state.fetching || !state.token;
  const isExchanging = !!state.exchanging;
  const hasExited = !isFetching && !isExchanging && state.exitStatus !== null;
  return (
    <Card
      data-testid="plaid-live-link-panel"
      className="mb-4 border-slate-200">
      <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
            <Banknote className="h-4 w-4" />
          </span>
          <div>
            <p className="text-sm font-semibold text-slate-900">
              {isFetching
                ? "Preparing Plaid Link…"
                : isExchanging
                  ? "Finishing connection…"
                  : hasExited
                    ? "Plaid Link closed"
                    : "Plaid Link is open"}
            </p>
            <p className="text-xs text-slate-500" data-testid="plaid-live-link-hint">
              {isFetching ? (
                <>Fetching a link token…</>
              ) : isExchanging ? (
                <>Exchanging public token with our backend.</>
              ) : hasExited ? (
                <>
                  {state.exitStatus ? `(${state.exitStatus}) ` : ""}
                  Click <span className="font-medium">Open Plaid Link</span> to try again,
                  or Dismiss to cancel.
                </>
              ) : (
                <>If the Plaid window didn&apos;t appear, click Open Plaid Link below.</>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isFetching || isExchanging ? (
            <span className="inline-flex items-center gap-1 text-sm text-slate-500" data-testid="plaid-exchanging">
              <Loader2 className="h-4 w-4 animate-spin" /> {isExchanging ? "Exchanging…" : "Preparing…"}
            </span>
          ) : (
            <Suspense fallback={
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading Plaid Link…
              </div>
            }>
              <PlaidLinkLauncher
                linkToken={state.token}
                onPublicToken={onPublicToken}
                onExit={onExit}
                autoOpen={state.exitStatus === null /* don't re-auto-open after a cancel */}
              />
            </Suspense>
          )}
          <Button
            variant="ghost"
            onClick={onDismiss}
            disabled={isExchanging}
            data-testid="plaid-live-link-dismiss-btn">
            Dismiss
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function AccountRow({ acct, cards, draft, onChange }) {
  const cardOptions = useMemo(() => cards.filter((c) => c.active), [cards]);
  return (
    <TableRow data-testid={`plaid-account-row-${acct.account_id}`}>
      <TableCell>
        <div className="flex flex-col">
          <span className="text-sm font-medium text-slate-900">{acct.name}</span>
          <span className="text-xs text-slate-500">
            <span className="font-mono">···{acct.mask || "----"}</span>
            {acct.type && <span className="ml-2">· {acct.type}{acct.subtype ? `/${acct.subtype}` : ""}</span>}
          </span>
        </div>
      </TableCell>
      <TableCell>
        <Select
          value={draft.our_card_id || NONE}
          onValueChange={(v) => onChange({ ...draft, our_card_id: v === NONE ? null : v })}>
          <SelectTrigger data-testid={`plaid-card-select-${acct.account_id}`} className="w-full">
            <SelectValue placeholder="Choose a card" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>— No mapping —</SelectItem>
            {cardOptions.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.label} · {c.last4} ({c.type})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell>
        {acct.auto_mapped ? (
          <Badge className="bg-violet-50 text-violet-700 hover:bg-violet-50" data-testid={`plaid-auto-badge-${acct.account_id}`}>
            <Wand2 className="mr-1 h-3 w-3" /> Auto-mapped
          </Badge>
        ) : (
          <span className="text-xs text-slate-400">manual</span>
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Switch
            checked={!!draft.confirmed}
            onCheckedChange={(v) => onChange({ ...draft, confirmed: v })}
            data-testid={`plaid-confirm-switch-${acct.account_id}`}
            disabled={!draft.our_card_id}
          />
          {draft.confirmed ? (
            <span className="inline-flex items-center text-xs font-medium text-emerald-700">
              <CheckCircle2 className="mr-1 h-3 w-3" /> Confirmed
            </span>
          ) : (
            <span className="text-xs text-slate-400">unconfirmed</span>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

function MigrationBanner({ items }) {
  const STORAGE_KEY = "plaid_migration_banner_dismissed_at";
  const migrated = (items || []).filter((it) => it.status === "migrated");
  const [dismissed, setDismissed] = useState(() => {
    try { return !!localStorage.getItem(STORAGE_KEY); } catch { return false; }
  });
  if (dismissed || migrated.length === 0) return null;
  const handleDismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch (err) { console.error("plaid-migration-banner: localStorage.setItem failed", err); }
    setDismissed(true);
  };
  return (
    <Alert
      data-testid="plaid-migration-banner"
      className="mb-4 border-amber-300 bg-amber-50 text-amber-900">
      <AlertTriangle className="h-4 w-4 text-amber-700" />
      <AlertDescription className="flex w-full items-center justify-between gap-3">
        <span>
          <span className="font-semibold">Plaid moved to Production</span> — please
          re-link each institution. {migrated.length} previously-linked item{migrated.length === 1 ? "" : "s"}{" "}
          {migrated.length === 1 ? "is" : "are"} frozen and no longer syncing.
        </span>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          data-testid="plaid-migration-banner-dismiss"
          className="rounded p-1 text-amber-700/70 hover:bg-amber-100 hover:text-amber-900">
          ×
        </button>
      </AlertDescription>
    </Alert>
  );
}

function ItemCard({ item, cards, onChanged, onDelete, onSync }) {
  const [drafts, setDrafts] = useState(() =>
    Object.fromEntries((item.accounts || []).map((a) => [
      a.account_id,
      { account_id: a.account_id, our_card_id: a.our_card_id || null, confirmed: !!a.confirmed },
    ]))
  );
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [lastSyncResult, setLastSyncResult] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Sync drafts when item refreshes
  useEffect(() => {
    setDrafts(Object.fromEntries((item.accounts || []).map((a) => [
      a.account_id,
      { account_id: a.account_id, our_card_id: a.our_card_id || null, confirmed: !!a.confirmed },
    ])));
  }, [item]);

  const dirty = useMemo(() => {
    return (item.accounts || []).some((a) => {
      const d = drafts[a.account_id];
      return !d || (d.our_card_id || null) !== (a.our_card_id || null) || !!d.confirmed !== !!a.confirmed;
    });
  }, [drafts, item.accounts]);

  const saveMappings = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/plaid/items/${item.id}/card-mappings`, {
        mappings: Object.values(drafts),
      });
      toast.success("Card mappings saved");
      onChanged?.(r.data);
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };

  const syncNow = async () => {
    setSyncing(true); setLastSyncResult(null);
    try {
      const r = await api.post(`/plaid/items/${item.id}/sync`, {});
      const totalLines = (r.data.batches || []).reduce((s, b) => s + (b.total_lines || 0), 0);
      const matched = (r.data.batches || []).reduce((s, b) => s + (b.matched || 0), 0);
      setLastSyncResult({
        batches: r.data.batches?.length || 0,
        skipped: r.data.skipped_accounts?.length || 0,
        total_lines: totalLines,
        matched,
      });
      if ((r.data.batches || []).length > 0) {
        toast.success(`Synced ${totalLines} lines across ${r.data.batches.length} batch(es)`);
      } else {
        toast.info("Nothing new to import.");
      }
      onChanged?.();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSyncing(false); }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/plaid/items/${item.id}`);
      toast.success("Plaid item disconnected");
      onDelete?.(item.id);
    } catch (e) { toast.error(extractError(e)); }
    finally { setDeleting(false); setConfirmDelete(false); }
  };

  const confirmedCount = (item.accounts || []).filter((a) => a.confirmed && a.our_card_id).length;
  const totalAccounts = (item.accounts || []).length;

  const isMigrated = item.status === "migrated";

  return (
    <Card
      data-testid={`plaid-item-${item.id}`}
      className={`border-slate-200 ${isMigrated ? "bg-slate-50 opacity-80" : ""}`}>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <CardTitle className="flex items-center gap-2 text-lg">
            <span className={`inline-flex h-8 w-8 items-center justify-center rounded-md ${isMigrated ? "bg-slate-200" : "bg-slate-100"}`}>
              <Landmark className={`h-4 w-4 ${isMigrated ? "text-slate-400" : "text-slate-700"}`} />
            </span>
            <span data-testid={`plaid-item-name-${item.id}`} className={isMigrated ? "text-slate-500 line-through" : ""}>
              {item.institution_name || "Unknown bank"}
            </span>
            {isMigrated ? (
              <Badge
                className="bg-amber-100 text-amber-800 hover:bg-amber-100"
                data-testid={`plaid-item-migrated-badge-${item.id}`}>
                Inactive (migrated)
              </Badge>
            ) : (
              <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                {confirmedCount}/{totalAccounts} mapped
              </Badge>
            )}
          </CardTitle>
          <CardDescription className="mt-1 grid grid-cols-1 gap-x-6 gap-y-0.5 text-xs text-slate-500 sm:grid-cols-2">
            <span>Item ID: <span className="font-mono text-[11px]">{item.item_id}</span></span>
            <span>Linked: {fmtDateTime(item.created_at)}</span>
            <span data-testid={`plaid-last-sync-${item.id}`}>
              Last sync: {item.last_synced_at ? fmtDateTime(item.last_synced_at) : <span className="text-amber-700">never</span>}
            </span>
            <span data-testid={`plaid-lines-${item.id}`}>
              Transactions imported: <span className="font-mono">{item.total_lines_imported || 0}</span>
              {item.batches_imported ? <span className="text-slate-400"> · across {item.batches_imported} batch(es)</span> : null}
            </span>
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={syncNow}
            disabled={syncing || confirmedCount === 0}
            data-testid={`plaid-sync-btn-${item.id}`}>
            <RefreshCw className={`mr-1 h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing…" : "Sync now"}
          </Button>
          <Button
            variant="ghost"
            onClick={() => setConfirmDelete(true)}
            data-testid={`plaid-delete-btn-${item.id}`}
            className="text-rose-600 hover:bg-rose-50 hover:text-rose-700">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        {totalAccounts === 0 ? (
          <p className="text-sm text-slate-500">No accounts on this item.</p>
        ) : (
          <>
            {confirmedCount === 0 && (
              <Alert className="mb-3 border-amber-200 bg-amber-50" data-testid={`plaid-no-mappings-${item.id}`}>
                <AlertDescription className="flex items-center gap-2 text-amber-900">
                  <AlertTriangle className="h-4 w-4" />
                  Confirm at least one card mapping before syncing.
                </AlertDescription>
              </Alert>
            )}
            <div className="overflow-x-auto rounded-md border border-slate-200">
              <Table data-testid={`plaid-accounts-table-${item.id}`}>
                <TableHeader>
                  <TableRow>
                    <TableHead>Account</TableHead>
                    <TableHead className="min-w-[260px]">Maps to our card</TableHead>
                    <TableHead>Origin</TableHead>
                    <TableHead>Confirmed</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(item.accounts || []).map((a) => (
                    <AccountRow
                      key={a.account_id}
                      acct={a}
                      cards={cards}
                      draft={drafts[a.account_id] || { our_card_id: null, confirmed: false }}
                      onChange={(d) => setDrafts((prev) => ({ ...prev, [a.account_id]: d }))}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>

            {lastSyncResult && (
              <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900" data-testid={`plaid-sync-result-${item.id}`}>
                Last sync: <strong>{lastSyncResult.total_lines}</strong> line(s) in{" "}
                <strong>{lastSyncResult.batches}</strong> batch(es) · <strong>{lastSyncResult.matched}</strong> matched
                {lastSyncResult.skipped ? <> · <strong>{lastSyncResult.skipped}</strong> account(s) skipped (no card mapping)</> : null}
              </div>
            )}

            <div className="mt-3 flex justify-end">
              <Button
                onClick={saveMappings}
                disabled={!dirty || saving}
                data-testid={`plaid-save-mappings-${item.id}`}
                className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90">
                {saving ? "Saving…" : "Save mappings"}
              </Button>
            </div>
          </>
        )}
      </CardContent>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent data-testid={`plaid-delete-dialog-${item.id}`} className="max-w-md">
          <DialogHeader>
            <DialogTitle>Disconnect {item.institution_name}?</DialogTitle>
            <DialogDescription>
              The Plaid link will be removed. Imported statement batches and reconciled lines stay intact.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDelete(false)} disabled={deleting}>Cancel</Button>
            <Button
              onClick={doDelete}
              disabled={deleting}
              data-testid={`plaid-delete-confirm-${item.id}`}
              className="bg-rose-600 text-white hover:bg-rose-700">
              {deleting ? "Disconnecting…" : "Disconnect"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function SourceBadge({ source }) {
  if (source === "webhook") {
    return (
      <Badge className="bg-sky-50 text-sky-700 hover:bg-sky-50" data-testid="sync-source-webhook">
        <Webhook className="mr-1 h-3 w-3" /> webhook
      </Badge>
    );
  }
  if (source === "schedule") {
    return (
      <Badge className="bg-violet-50 text-violet-700 hover:bg-violet-50" data-testid="sync-source-schedule">
        <CalendarClock className="mr-1 h-3 w-3" /> scheduled
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="bg-slate-100 text-slate-700">{source || "—"}</Badge>
  );
}

function ProcessedBadge({ processed, error }) {
  if (processed) {
    return (
      <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50">
        <CheckCircle2 className="mr-1 h-3 w-3" /> processed
      </Badge>
    );
  }
  const isSig = error && /signature/i.test(error);
  return (
    <Badge className={isSig ? "bg-rose-50 text-rose-700 hover:bg-rose-50" : "bg-amber-50 text-amber-800 hover:bg-amber-50"}>
      <AlertTriangle className="mr-1 h-3 w-3" /> {isSig ? "rejected" : "failed"}
    </Badge>
  );
}

function fmtSyncSummary(s) {
  if (!s || typeof s !== "object") return "—";
  const parts = [];
  if (s.batches != null) parts.push(`${s.batches} batch${s.batches === 1 ? "" : "es"}`);
  if (s.skipped_accounts) parts.push(`${s.skipped_accounts} skipped`);
  if (s.has_more) parts.push("more pending");
  return parts.length ? parts.join(" · ") : "no changes";
}

function RecentActivityPanel() {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = { days: 30, page_size: 30 };
      if (sourceFilter !== "all") params.source = sourceFilter;
      const r = await api.get("/plaid/sync-history", { params });
      setRows(r.data?.items || []);
    } catch (e) { setError(extractError(e)); }
    finally { setLoading(false); }
  }, [sourceFilter]);

  useEffect(() => { if (open) load(); }, [open, load]);

  return (
    <Card className="mb-4 border-slate-200" data-testid="plaid-recent-activity-panel">
      <CardContent className="p-3">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800"
            data-testid="plaid-activity-toggle">
            <Activity className="h-4 w-4" />
            Recent activity
            <span className="text-xs font-normal text-slate-500">(last 30 days, webhooks + scheduled)</span>
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {open && (
            <div className="ml-auto flex items-center gap-2">
              {[
                { k: "all", label: "All" },
                { k: "webhook", label: "Webhooks" },
                { k: "schedule", label: "Scheduled" },
              ].map((opt) => (
                <button
                  key={opt.k}
                  type="button"
                  onClick={() => setSourceFilter(opt.k)}
                  data-testid={`plaid-activity-filter-${opt.k}`}
                  className={`rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ${
                    sourceFilter === opt.k
                      ? "bg-[#0F1E3D] text-white ring-[#0F1E3D]"
                      : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50"
                  }`}>
                  {opt.label}
                </button>
              ))}
              <Button size="sm" variant="outline" onClick={load} disabled={loading}
                data-testid="plaid-activity-refresh">
                <RefreshCw className={`mr-1 h-3 w-3 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
            </div>
          )}
        </div>

        {open && (
          <div className="mt-3">
            {error && (
              <Alert variant="destructive" className="mb-2"><AlertDescription>{error}</AlertDescription></Alert>
            )}
            <div className="overflow-x-auto rounded-md border border-slate-200">
              <Table data-testid="plaid-activity-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="whitespace-nowrap">When</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Institution</TableHead>
                    <TableHead>Type / Code</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Result</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={6} className="py-6 text-center text-slate-500">
                      <Loader2 className="mx-auto h-4 w-4 animate-spin" /> Loading…
                    </TableCell></TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="py-6 text-center text-slate-500" data-testid="plaid-activity-empty">
                      No activity in the last 30 days.
                    </TableCell></TableRow>
                  ) : rows.map((r) => (
                    <TableRow key={r.id} data-testid={`plaid-activity-row-${r.id}`}>
                      <TableCell className="whitespace-nowrap text-xs text-slate-600">{fmtDateTime(r.received_at)}</TableCell>
                      <TableCell><SourceBadge source={r.source} /></TableCell>
                      <TableCell className="text-sm text-slate-700">
                        {r.institution_name || <span className="text-slate-400">—</span>}
                      </TableCell>
                      <TableCell className="text-xs">
                        <div className="font-mono text-slate-700">{r.webhook_type || "—"}</div>
                        {r.webhook_code && <div className="font-mono text-slate-500">{r.webhook_code}</div>}
                      </TableCell>
                      <TableCell><ProcessedBadge processed={r.processed} error={r.error} /></TableCell>
                      <TableCell className="text-xs text-slate-600">
                        {r.error ? (
                          <span className="text-rose-700" title={r.error}>{r.error.length > 60 ? `${r.error.slice(0, 60)}…` : r.error}</span>
                        ) : (
                          <span>{fmtSyncSummary(r.sync_summary)}</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <p className="mt-2 text-xs text-slate-500" data-testid="plaid-activity-count">
              {rows.length} event{rows.length === 1 ? "" : "s"} shown.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function PlaidIntegrationsAdmin() {
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [error, setError] = useState("");
  const [cards, setCards] = useState([]);
  const [connectOpen, setConnectOpen] = useState(false);
  // Live-link state lives at the page level (NOT inside the Radix Dialog) so
  // the Plaid Link iframe doesn't inherit `pointer-events: none` from the
  // dialog's body lock. See the "Plaid Link blocked by Radix Dialog" issue.
  const [livePanel, setLivePanel] = useState(null);
  // livePanel shape: { token: string, exitStatus: string|null, exchanging: bool }

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const r = await api.get("/integrations/plaid/status");
      setStatus(r.data);
    } catch (e) { setError(extractError(e)); }
    finally { setStatusLoading(false); }
  }, []);

  const loadItems = useCallback(async () => {
    setItemsLoading(true);
    try {
      const [it, cs] = await Promise.all([
        api.get("/plaid/items"),
        api.get("/cards", { params: { active_only: true } }),
      ]);
      setItems(it.data?.items || []);
      setCards(cs.data || []);
    } catch (e) { setError(extractError(e)); }
    finally { setItemsLoading(false); }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadStatus(); loadItems(); }, [loadStatus, loadItems]);

  const onItemChanged = (updatedItem) => {
    if (!updatedItem) { loadItems(); loadStatus(); return; }
    setItems((prev) => prev.map((p) => (p.id === updatedItem.id ? updatedItem : p)));
  };
  const onItemDeleted = (id) => {
    setItems((prev) => prev.filter((p) => p.id !== id));
    loadStatus();
  };
  const onConnected = () => { loadItems(); loadStatus(); };

  // Called by ConnectBankDialog when admin clicks "Open Plaid Link" in live
  // mode. We close the dialog FIRST (so Radix releases the body lock) and
  // only then mount the inline launcher.
  const requestLiveLink = useCallback(async () => {
    // Mount the live panel immediately with a "fetching" placeholder so the
    // admin gets visual feedback while we wait for /plaid/link-token.
    setLivePanel({ token: null, exitStatus: null, exchanging: false, fetching: true });
    try {
      const r = await api.post("/plaid/link-token", {});
      setLivePanel({ token: r.data.link_token, exitStatus: null, exchanging: false, fetching: false });
    } catch (e) {
      setLivePanel(null);
      throw e;  // bubble to the dialog's startLiveLink toast
    }
  }, []);

  const handleLivePublicToken = useCallback(async (publicToken) => {
    setLivePanel((s) => (s ? { ...s, exchanging: true } : s));
    try {
      const r = await api.post("/plaid/exchange-public-token", { public_token: publicToken });
      toast.success(`Linked ${r.data?.institution_name || "institution"}`);
      setLivePanel(null);
      loadItems(); loadStatus();
    } catch (e) {
      toast.error(extractError(e));
      setLivePanel((s) => (s ? { ...s, exchanging: false, exitStatus: "exchange_failed" } : s));
    }
  }, [loadItems, loadStatus]);

  const handleLiveExit = useCallback((err, metadata) => {
    const exitStatus = metadata?.status ?? "closed";

    if (err && err.error_code) {
      toast.error(String(err.error_message || err.display_message || err.error_code));
    } else if (metadata?.status && DEFINITIVE_EXIT_STATUSES.has(metadata.status)) {
      toast.message("Connect cancelled", {
        description: "Click \u201cOpen Plaid Link\u201d to try again, or Dismiss to clear.",
      });
    }
    // (transient overlay-click exits with no status stay silent — but we still
    // record an exitStatus so the panel's retry hint can render.)
    setLivePanel((s) => (s ? { ...s, exitStatus } : s));
  }, []);

  return (
    <div data-testid="plaid-integrations-admin">
      <PageHeader
        title="Plaid integrations"
        subtitle="Link bank/credit-card institutions and map their accounts to your cards. Sync pulls transactions into the statement pipeline."
        action={
          <Button
            onClick={() => setConnectOpen(true)}
            data-testid="plaid-connect-btn"
            className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90">
            <Banknote className="mr-1 h-4 w-4" /> Connect bank
          </Button>
        }
      />

      {error && <Alert variant="destructive" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

      <StatusBanner status={status} loading={statusLoading} onRefresh={loadStatus} />

      <MigrationBanner items={items} />

      <RecentActivityPanel />

      <LiveLinkPanel
        state={livePanel}
        onPublicToken={handleLivePublicToken}
        onExit={handleLiveExit}
        onDismiss={() => setLivePanel(null)}
      />

      <div className="space-y-4">
        {itemsLoading ? (
          <Card className="border-slate-200"><CardContent className="p-6 text-sm text-slate-500">Loading linked items…</CardContent></Card>
        ) : items.length === 0 ? (
          <Card className="border-dashed border-slate-300 bg-white" data-testid="plaid-empty-state">
            <CardContent className="flex flex-col items-center justify-center gap-3 p-10 text-center">
              <Building2 className="h-10 w-10 text-slate-300" />
              <div>
                <p className="font-medium text-slate-900">No banks linked yet</p>
                <p className="mt-1 text-sm text-slate-500">
                  Connect your first institution to start importing card transactions automatically.
                </p>
              </div>
              <Button
                onClick={() => setConnectOpen(true)}
                data-testid="plaid-connect-empty-btn"
                className="mt-2 bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90">
                <Banknote className="mr-1 h-4 w-4" /> Connect bank
              </Button>
            </CardContent>
          </Card>
        ) : (
          items.map((it) => (
            <ItemCard
              key={it.id}
              item={it}
              cards={cards}
              onChanged={onItemChanged}
              onDelete={onItemDeleted}
            />
          ))
        )}
      </div>

      <ConnectBankDialog
        open={connectOpen}
        onOpenChange={setConnectOpen}
        mode={status?.mode || "mock"}
        onConnected={onConnected}
        onRequestLiveLink={requestLiveLink}
      />
    </div>
  );
}
